/**
 * CUSTOMER ADDRESSES CUSTOM ELEMENT
 *
 * Wraps the legacy customers/addresses template markup and provides:
 * - Progressive address entry toggling (`[data-address-manual-toggle]`)
 * - Country -> province select wiring. Country options come from Shopify's
 *   `all_country_option_tags`, which carries the available provinces on each
 *   option as a `data-provinces` JSON attribute. `data-default` on each select
 *   holds the saved value to restore.
 * - Address deletion. Shopify's legacy flow deletes an address by POSTing
 *   `_method=delete` to the address URL (`[data-address-delete="<url>"]`).
 *
 * The "new" and "edit" forms themselves are shown via <x-modal> (see
 * account-view-addresses.liquid) — this element only handles what happens
 * inside the form once it's open.
 */

export class CustomerAddresses extends HTMLElement {
  static MANUAL_CLASS = 'is-show-all'

  connectedCallback() {
    this.addEventListener('click', this.handleClick)
    this.addEventListener('change', this.handleChange)
    this.initCountrySelects()
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('change', this.handleChange)
  }

  handleClick = (event: Event) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-address-delete], [data-address-manual-toggle]'
    )
    if (!trigger || !this.contains(trigger)) return

    if (trigger.dataset.addressDelete) {
      const message = trigger.dataset.confirmMessage || 'Are you sure you wish to delete this address?'
      if (window.confirm(message)) {
        this.deleteAddress(trigger.dataset.addressDelete)
      }
    } else if (trigger.hasAttribute('data-address-manual-toggle')) {
      this.showManualFields(trigger)
    }
  }

  handleChange = (event: Event) => {
    const select = event.target as HTMLSelectElement
    if (select.matches('[data-address-country]')) {
      this.updateProvinces(select)
    }
  }

  showManualFields(trigger: HTMLElement) {
    const root = trigger.closest<HTMLElement>('[data-address-root]')
    if (!root) return

    root.classList.add(CustomerAddresses.MANUAL_CLASS)
  }

  initCountrySelects() {
    this.querySelectorAll<HTMLSelectElement>('[data-address-country]').forEach((select) => {
      const defaultValue = select.dataset.default
      if (defaultValue) select.value = defaultValue
      this.updateProvinces(select)
    })
  }

  updateProvinces(countrySelect: HTMLSelectElement) {
    const form = countrySelect.closest('form')
    const provinceSelect = form?.querySelector<HTMLSelectElement>('[data-address-province]')
    const provinceWrapper = form?.querySelector<HTMLElement>('[data-address-province-wrapper]')
    if (!provinceSelect || !provinceWrapper) return

    const selectedOption = countrySelect.selectedOptions[0]
    let provinces: Array<[string, string]> = []
    try {
      provinces = JSON.parse(selectedOption?.dataset.provinces || '[]')
    } catch {
      provinces = []
    }

    provinceSelect.innerHTML = ''
    provinces.forEach(([value, label]) => {
      const option = document.createElement('option')
      option.value = value
      option.textContent = label
      provinceSelect.append(option)
    })

    const defaultValue = provinceSelect.dataset.default
    if (defaultValue) provinceSelect.value = defaultValue

    provinceWrapper.hidden = provinces.length === 0
    provinceSelect.disabled = provinces.length === 0
  }

  deleteAddress(url: string) {
    const form = document.createElement('form')
    form.method = 'post'
    form.action = url

    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'delete'
    form.append(methodInput)

    document.body.append(form)
    form.submit()
  }
}
